import { useCallback, useEffect, useMemo, useState } from "react";
import { FileSignature, Search, Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { cn, initialsOf } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";
import { fmtDate } from "@/pages/Reperes";
import StatusTabs from "@/components/prospecting/StatusTabs";
import WorkspaceBreadcrumb from "@/components/prospecting/WorkspaceBreadcrumb";
import MandateDetail from "@/components/mandates/MandateDetail";

export const CLIENT_STATES = ["TO_LAUNCH", "SEARCHING", "NEGOTIATING", "RENEWAL_DUE", "ENDED"];
export const CLIENT_STATE_FR = {
  TO_LAUNCH: tfr("À lancer"), SEARCHING: "En recherche", NEGOTIATING: tfr("En négociation"),
  RENEWAL_DUE: tfr("À renouveler"), ENDED: tfr("Terminés"),
};

export default function MandatsSignes() {
  const { tf } = useLang();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("SEARCHING");
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [fSport, setFSport] = useState("all");
  const [fLevel, setFLevel] = useState("all");
  const [sort, setSort] = useState("deadline");
  const [mobileOpen, setMobileOpen] = useState(false);

  const load = useCallback(async () => {
    try { setData((await api.get("/mandats-signes")).data); }
    catch { toast.error(tf("Erreur de chargement")); }
  }, [tf]);
  useEffect(() => { load(); }, [load]);

  const clients = useMemo(() => data?.clients || [], [data]);
  const counts = data?.counts || {};
  const sports = useMemo(() => [...new Set(clients.map((c) => c.sport))], [clients]);
  const levels = useMemo(() => [...new Set(clients.map((c) => c.level).filter(Boolean))], [clients]);

  const list = useMemo(() => {
    let arr = clients.filter((c) => c.client_state === tab);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      arr = arr.filter((c) => `${c.name} ${c.club} ${c.position}`.toLowerCase().includes(s));
    }
    if (fSport !== "all") arr = arr.filter((c) => c.sport === fSport);
    if (fLevel !== "all") arr = arr.filter((c) => c.level === fLevel);
    const dl = (c) => c.next_deadline?.days ?? 9999;
    const me = (c) => c.mandate?.end_date || "9999";
    if (sort === "deadline") arr.sort((a, b) => dl(a) - dl(b));
    else if (sort === "mandate_end") arr.sort((a, b) => me(a).localeCompare(me(b)));
    else if (sort === "last_exchange") arr.sort((a, b) => (b.last_interaction_at || "").localeCompare(a.last_interaction_at || ""));
    else if (sort === "clubs") arr.sort((a, b) => b.clubs_targeted - a.clubs_targeted);
    else if (sort === "offers") arr.sort((a, b) => b.offers_count - a.offers_count);
    else arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [clients, tab, q, fSport, fLevel, sort]);

  const current = useMemo(
    () => clients.find((c) => c.id === selected && c.client_state === tab) || list[0] || null,
    [clients, selected, list, tab]
  );

  const exportCsv = () => {
    const head = ["Client", "Sport", "Poste", "Niveau", "Club actuel", "Fin du mandat", "Clubs approchés", "Offres", "Prochaine échéance", "État"].map((s) => tfr(s));
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [head, ...list.map((c) => [
      c.name, c.sport, c.position || "", c.level || "", c.club || "Sans club",
      c.mandate?.end_date || "", c.clubs_targeted, c.offers_count,
      c.next_deadline ? `${c.next_deadline.label} ${c.next_deadline.date}` : "",
      CLIENT_STATE_FR[c.client_state],
    ])].map((r) => r.map(esc).join(";"));
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const el = document.createElement("a");
    el.href = URL.createObjectURL(blob);
    el.download = "athlynx_mandats_signes.csv";
    el.click();
    URL.revokeObjectURL(el.href);
  };

  return (
    <div className="animate-fade-in p-4 md:p-6 space-y-4 md:space-y-5 max-w-[1600px] mx-auto min-w-0" data-testid="mandates-page">
      {/* Breadcrumb + header */}
      <div>
        <WorkspaceBreadcrumb currentLabel={tfr("Mandats signés")} testid="mandates-breadcrumb-dashboard" />
        <div className="flex flex-wrap items-start justify-between gap-4 mt-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-11 w-11 rounded-xl bg-athlynx/10 border border-athlynx/25 flex items-center justify-center shrink-0">
              <FileSignature className="h-5 w-5 text-athlynx" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight">{tf("Mandats signés")}</h1>
              <p className="text-sm font-medium text-foreground/80">{tf("Vos clients sous mandat, en recherche de club.")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{tf("Suivez leurs projets, vos démarches et transformez les opportunités en placements.")}</p>
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card px-4 py-3 flex items-center gap-4" data-testid="mandates-summary">
            <div>
              <div className="text-3xl font-bold text-foreground tabular-nums leading-none" data-testid="mandates-stock">{counts.total ?? "—"}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{tf("clients sous mandat")}</div>
            </div>
            <div className="text-[11px] text-muted-foreground border-l border-border pl-4">
              +{data?.flow_7d?.entries ?? 0} {tf((data?.flow_7d?.entries ?? 0) > 1 ? "entrées" : "entrée")} · {data?.flow_7d?.exits ?? 0} {tf((data?.flow_7d?.exits ?? 0) > 1 ? "sorties" : "sortie")} {tf("ces 7 jours")}
            </div>
          </div>
        </div>
      </div>

      {/* Client state tabs */}
      <StatusTabs containerTestId="mandates-tabs" value={tab}
        onChange={(s) => { setTab(s); setSelected(null); }}
        tabs={CLIENT_STATES.map((s) => ({
          id: s, label: tf(CLIENT_STATE_FR[s]), count: counts[s] ?? 0,
          tone: { SEARCHING: "info", NEGOTIATING: "info", RENEWAL_DUE: "warning" }[s] || "neutral",
          testid: `mandates-tab-${s}`, countTestid: `mandates-count-${s}`,
        }))} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[170px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tf("Rechercher un client…")}
            className="h-9 pl-8 text-sm" data-testid="mandates-search" />
        </div>
        {sports.length > 1 && (
          <Select value={fSport} onValueChange={setFSport}>
            <SelectTrigger className="h-9 w-[120px] text-xs" data-testid="mandates-filter-sport"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{tf("Tous les sports")}</SelectItem>
              {sports.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {levels.length > 1 && (
          <Select value={fLevel} onValueChange={setFLevel}>
            <SelectTrigger className="h-9 w-[130px] text-xs" data-testid="mandates-filter-level"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{tf("Niveau : tous")}</SelectItem>
              {levels.map((l) => <SelectItem key={l} value={l}>{tfr(l)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-9 w-[200px] text-xs" data-testid="mandates-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="deadline">{tf("Prochaine échéance")}</SelectItem>
            <SelectItem value="mandate_end">{tf("Fin du mandat")}</SelectItem>
            <SelectItem value="last_exchange">{tf("Dernier échange")}</SelectItem>
            <SelectItem value="clubs">{tf("Clubs approchés")}</SelectItem>
            <SelectItem value="offers">{tf("Nombre d'offres")}</SelectItem>
            <SelectItem value="name">{tf("Nom")}</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setQ(""); setFSport("all"); setFLevel("all"); setSort("deadline"); }} data-testid="mandates-reset-filters">
          {tf("Réinitialiser")}
        </Button>
        <Button variant="outline" size="sm" className="h-9 text-xs" onClick={exportCsv} data-testid="mandates-export">
          <Download className="h-3.5 w-3.5 mr-1.5" />{tf("Exporter")}
        </Button>
      </div>

      {/* List + Detail */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-4 xl:gap-5 items-start min-w-0">
        <div className="rounded-2xl border border-border bg-card min-w-0 overflow-hidden" data-testid="mandates-list">
          <div className="hidden sm:flex items-center gap-3 px-3 py-2 border-b border-border/70 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
            <span className="flex-1 min-w-0">{tf("Client")}</span>
            <span className="hidden md:block w-24 shrink-0">{tf("Mandat")}</span>
            <span className="w-16 shrink-0 text-center">{tf("Suivi")}</span>
            <span className="hidden lg:block w-32 shrink-0 text-right">{tf("Prochaine échéance")}</span>
          </div>
          {!data ? (
            <div className="p-4 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-xl bg-secondary/60 animate-pulse" />)}</div>
          ) : list.length === 0 ? (
            <div className="py-14 text-center text-sm text-muted-foreground px-4" data-testid="mandates-empty">
              {q.trim() || fSport !== "all" || fLevel !== "all"
                ? tf("Aucun client ne correspond à ces filtres.")
                : tab === "ENDED" ? tf("Aucun mandat terminé.") : tf("Aucun client sous mandat pour le moment.")}
            </div>
          ) : (
            <ul className="divide-y divide-border/70">
              {list.map((c) => (
                <li key={c.id}>
                  <div role="button" tabIndex={0}
                    onClick={() => { setSelected(c.id); if (window.matchMedia("(max-width: 1279px)").matches) setMobileOpen(true); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { setSelected(c.id); if (window.matchMedia("(max-width: 1279px)").matches) setMobileOpen(true); } }}
                    className={cn("w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors cursor-pointer min-w-0",
                      current?.id === c.id ? "bg-secondary/60 border-l-2 border-l-foreground/70" : "hover:bg-secondary/50 border-l-2 border-l-transparent")}
                    data-testid={`mandates-row-${c.id}`}>
                    <Avatar className="h-9 w-9 shrink-0">
                      <AvatarImage src={c.photo_url} alt="" />
                      <AvatarFallback className="text-[11px]">{initialsOf(c.name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-semibold truncate">{c.name}</span>
                        {c.minor && <span className="shrink-0 text-[10px] font-semibold uppercase rounded-full border border-warning/40 bg-warning/10 text-warning px-1.5 py-px">{tf("Mineur")}</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {[c.position, c.level, c.club || tf("Sans club"), c.age ? `${c.age} ans` : null].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <div className="hidden md:block w-24 shrink-0">
                      {c.mandate?.end_date ? (
                        <>
                          <div className="text-xs tabular-nums">{fmtDate(c.mandate.end_date)}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{tf(c.mandate.remaining_label || "")}</div>
                        </>
                      ) : <span className="text-xs text-muted-foreground/60">—</span>}
                    </div>
                    <div className="w-16 shrink-0 text-center text-[11px] tabular-nums text-muted-foreground" data-testid={`activity-${c.id}`}>
                      {c.clubs_targeted} {tf("clubs")}<br />{c.offers_count} {tf(c.offers_count > 1 ? "offres" : "offre")}
                    </div>
                    <div className="hidden lg:block w-32 shrink-0 text-right">
                      {c.next_deadline ? (
                        <>
                          <div className="text-[11px] truncate">{tf(c.next_deadline.label)}</div>
                          <div className={cn("text-xs tabular-nums font-medium",
                            c.next_deadline.days <= 7 ? "text-athlynx" : c.next_deadline.days <= 30 ? "text-warning" : "text-muted-foreground")}>
                            {fmtDate(c.next_deadline.date)} · {c.next_deadline.days === 0 ? tf("aujourd'hui") : `${tf("dans")} ${c.next_deadline.days} j`}
                          </div>
                        </>
                      ) : <span className="text-xs text-muted-foreground/60">—</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="hidden xl:block min-w-0">
          {current ? <MandateDetail key={current.id} c={current} tf={tf} onChanged={load} /> : (
            <div className="rounded-2xl border border-dashed border-border py-16 text-center text-sm text-muted-foreground">
              {tf("Sélectionnez un client")}
            </div>
          )}
        </div>
      </div>

      <div className="xl:hidden">
        <Sheet open={mobileOpen && !!current} onOpenChange={setMobileOpen}>
          <SheetContent side="bottom" className="inset-x-2 min-h-[60dvh] max-h-[calc(100dvh-104px)] overflow-y-auto rounded-t-2xl border p-4 pb-[max(16px,env(safe-area-inset-bottom))]">
            <SheetTitle className="sr-only">{current?.name || tf("Client")}</SheetTitle>
            <SheetDescription className="sr-only">{tf("Espace de travail du client sélectionné")}</SheetDescription>
            {current && <MandateDetail key={current.id} c={current} tf={tf} onChanged={load} />}
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
